// FILE: frontend/client/src/context/AuthContext.jsx

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';

// ✅ IMPORT THE SECURE API BRIDGE (must match Profile.js import)
import { api } from '../services/apiClient';

// 💡 MOCK UTILS TO PREVENT BUILD ERRORS IN PREVIEW
const getQuotas = () => ({});
const SUBSCRIPTION_TIERS = { FREEMIUM: 'freemium', STARTER: 'starter', GROWTH: 'growth', SCALE: 'scale', EXECUTIVE: 'executive' };

// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

let app;
let auth;
try {
  if (!getApps().length) {
    app = initializeApp(firebaseConfig);
  } else {
    app = getApps()[0];
  }
  auth = getAuth(app);
  // Force durable local persistence (IndexedDB → localStorage fallback inside the SDK).
  // Without this explicit call, some browsers (Edge tracking prevention, strict
  // third-party cookie settings) can silently fall back to in-memory persistence,
  // which wipes the session on any full-page redirect — exactly what happens
  // when a user goes to Instagram's OAuth page and gets redirected back.
  setPersistence(auth, browserLocalPersistence).catch((err) => {
    console.error('[AuthContext] Failed to set persistence:', err.message);
  });
} catch (error) {
  console.error('Firebase initialization error', error);
}

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ FIXED: apiClient already unwraps response.data, so response IS the data object
  const fetchUserData = useCallback(async () => {
    if (!auth?.currentUser) return;
    try {
      const response = await api.get('/users/profile');
      // apiClient returns { success: true, ...response.data }, so check response.success
      if (response && response.success) {
        // apiClient spreads response.data onto the response object at the top level.
        // The user profile fields (subscriptionPlan, brandName, etc.) live directly
        // on `response`, not nested under response.data.
        // Handle both shapes defensively.
        const profileData = (response.data && response.data.subscriptionPlan)
          ? response.data   // nested shape
          : response;       // flat shape (current backend)
        setUserData(profileData);
        console.log('[AuthContext] MongoDB profile synced, plan:', profileData?.subscriptionPlan);

        // Identify user in PostHog if available globally
        if (typeof window !== 'undefined' && window.posthog) {
          window.posthog.identify(auth.currentUser.uid, {
            email: auth.currentUser.email,
            subscriptionPlan: profileData?.subscriptionPlan
          });
        }
      } else {
        console.warn('[AuthContext] Profile fetch returned non-success:', response);
      }
    } catch (error) {
      console.error('[AuthContext] Error fetching MongoDB profile:', error.message);
    }
  }, []);

  // Sync Firebase Auth with local state and trigger DB fetch
  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setCurrentUser(firebaseUser);
        await fetchUserData();
        setLoading(false);
        return;
      }

      // firebaseUser is null. This can mean a real logout, OR it can be a
      // transient state fired during the remount that happens right after
      // a full-page OAuth redirect (e.g. returning from Instagram's connect
      // flow) while Firebase is still rehydrating the session from storage.
      // Give it one short beat to resolve before treating it as a real logout,
      // so a brand-new social connection doesn't wipe the whole dashboard.
      await new Promise((resolve) => setTimeout(resolve, 600));
      const recheck = auth.currentUser;

      if (recheck) {
        setCurrentUser(recheck);
        await fetchUserData();
      } else {
        setCurrentUser(null);
        setUserData(null);
        if (typeof window !== 'undefined' && window.posthog) window.posthog.reset();
      }

      setLoading(false);
    });

    return unsubscribe;
  }, [fetchUserData]);

  // --- Auth Methods ---
  const login = (email, password) => signInWithEmailAndPassword(auth, email, password);
  const signup = (email, password) => createUserWithEmailAndPassword(auth, email, password);
  const logout = () => signOut(auth);
  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  // ✅ FIXED: Properly handle apiClient response format
  const updateUserProfile = async (data) => {
    try {
      // Optimistic UI update
      setUserData(prev => ({ ...prev, ...data }));

      // Hit the backend to persist changes
      const response = await api.put('/users/profile', data);

      if (response && response.success) {
        // Refresh from server to get clean synced state
        await fetchUserData();
        return { success: true, data: response.data || response };
      } else {
        // Revert optimistic update on failure
        await fetchUserData();
        return { 
          success: false, 
          error: response?.error || 'Failed to save profile',
          code: response?.code 
        };
      }
    } catch (error) {
      console.error('Profile update error:', error);
      // Revert optimistic update
      await fetchUserData();
      return { success: false, error: error.message || 'Network error during save.' };
    }
  };

  // ✅ NEW: Force refresh user data (call after plan changes, settings updates, etc.)
  const refreshUserData = useCallback(async () => {
    await fetchUserData();
  }, [fetchUserData]);

  const value = {
    currentUser,
    setUserData,
    userData,
    loading,
    login,
    signup,
    logout,
    resetPassword,
    fetchUserData,
    refreshUserData,
    updateUserProfile,
    firebaseInitialized: !!auth
  };

  if (!auth && !loading) {
    return (
      <div style={{ padding: '20px', margin: '20px', backgroundColor: '#fee', border: '2px solid #c00', borderRadius: '8px' }}>
        <h2>Firebase Configuration Error</h2>
        <p>Firebase authentication is not initialized. Please check your environment variables.</p>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}